import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axiosInstance";
import ToggleButton from "../../../components/ToggleButton/ToggleButton"; // Import your ToggleButton component
import "./AttendanceLocation.scss";
import IconButton from "../../../components/IconButton/IconButton";
import Pagination from "../../../components/Pagination/Pagination";
import GeneralTable from "../../../components/Table/GeneralTable";
import { toast } from "react-toastify";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";

const AttendanceLocation = ({ markPageAsCompleted }) => {
  const [location, setLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingIndex, setEditingIndex] = useState(null);
  const [error, setError] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [bulkUpdate] = useState(false); // Removed unused setBulkUpdate
  const [updatedLocations, setUpdatedLocations] = useState([]);
  const [timesheetRequired, setTimesheetRequired] = useState(false);
  const [loading, setLoading] = useState(false);

  const rowsPerPage = 10;

  // Fetch attendance locations
  const fetchLocations = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/attendance-types`,
      );
      const data = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      setLocations(data);
      setUpdatedLocations(data);
      if (data.length > 0) {
        markPageAsCompleted("Attendance Location");
      }
    } catch (error) {
      console.error("Error fetching attendance locations:", error);
    }
  }, [markPageAsCompleted]);

  // Fetch timesheet status
  const fetchTimesheetStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/company-settings/timesheet-Status`,
      );
      if (response.data.status === 200) {
        setTimesheetRequired(response.data.data === "true");
      }
    } catch (error) {
      console.error("Error fetching timesheet status:", error);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchTimesheetStatus();
  }, [fetchLocations, fetchTimesheetStatus]);

  const handleAddOrUpdate = async () => {
    setLoading(true);

    if (!location.trim()) {
      toast.error("Please enter a valid Attendance Location.");
      setLoading(false);
      return;
    }

    try {
      if (editingIndex !== null) {
        // Update existing location
        const locationToUpdate = locations[editingIndex];
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/attendance-types/batch`,
          [
            {
              attendanceTypeId: locationToUpdate.attendanceTypeId,
              attendanceType: location,
              companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
              timesheetRequired: timesheetRequired,
            },
          ],
        );
        toast.success("Attendance Location updated successfully!");
        setEditingIndex(null);
      } else {
        // Add new location
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/attendance-types`,
          {
            attendanceType: location,
            companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
            timesheetRequired: timesheetRequired,
          },
        );
        toast.success("Attendance Location added successfully!");
      }

      setLocation("");
      setShowCreateForm(false);
      await fetchLocations(); // Refresh list

      if (markPageAsCompleted) {
        markPageAsCompleted("Attendance Location");
      }
    } catch (error) {
      console.error("Error adding/updating attendance location:", error);

      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (index) => {
    const locationToDelete = locations[index];
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/attendance-types/${locationToDelete.attendanceTypeId}`,
      );
      await fetchLocations(); // Refetch the updated list after deletion
      toast.success("Location deleted successfully.");
    } catch (error) {
      console.error("Error deleting attendance location:", error);
    }
  };

  const handleEdit = (index) => {
    setLocation(locations[index].attendanceType);
    setEditingIndex(index);
    setShowCreateForm(true);
  };

  const handleToggleTimesheet = async (isOn) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/company-settings/update-timesheet`,
        {
          companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
          updateTimeSheet: isOn,
        },
      );
      if (response.data.status === 200) {
        setTimesheetRequired(isOn);
        toast.success(
          `Timesheet requirement ${isOn ? "enabled" : "disabled"}.`,
        );
      }
    } catch (error) {
      console.error("Error updating timesheet status:", error);
    }
  };

  const columns = [
    { header: "Attendance Type", accessor: "attendanceType" },
    { header: "Actions", accessor: "action" },
  ];

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentLocations = locations.slice(indexOfFirstRow, indexOfLastRow);

  const totalPages = Math.ceil(locations.length / rowsPerPage);

  const tableData = currentLocations.map((item, index) => {
    const originalIndex = locations.findIndex(
      (l) => l.attendanceTypeId === item.attendanceTypeId,
    );
    return {
      attendanceType: bulkUpdate ? (
        <input
          className="attendancelocation-input"
          value={updatedLocations[originalIndex]?.attendanceType || ""}
          onChange={(e) => {
            const newUpdatedLocations = [...updatedLocations];
            newUpdatedLocations[originalIndex] = {
              ...newUpdatedLocations[originalIndex],
              attendanceType: e.target.value,
            };
            setUpdatedLocations(newUpdatedLocations);
          }}
        />
      ) : (
        item.attendanceType
      ),
      action: (
        <div className="action-buttons">
          <IconButton
            icon="iconamoon:edit-light"
            onClick={() => handleEdit(originalIndex)}
            variant="edit"
            title="Edit Attendance Type"
          />

          <IconButton
            icon="fluent:delete-28-regular"
            onClick={() => handleDelete(originalIndex)}
            variant="delete"
            title="Delete Attendance Type"
          />
        </div>
      ),
    };
  });

  return (
    <div className="attendance-location-container">
      <h2 className="display6">Attendance Location</h2>
      {error && <div className="error-message">{error}</div>}

      <div className="single-row">
        <ButtonWithIcon
          className="create-role-button"
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            setLocation("");
            setEditingIndex(null);
          }}
          style={
            showCreateForm ? { backgroundColor: "#ff6692", color: "#fff" } : {}
          }
          icon={!showCreateForm ? "ic:round-plus" : ""}
          text={showCreateForm ? "Close Form" : "Create Location"}
        />

        <div className="department__search-input-container">
          <div className="toggle-Button">
            <label className="timesheet-label"> Timesheet required? </label>
            <ToggleButton
              isOn={timesheetRequired}
              onToggle={() => handleToggleTimesheet(!timesheetRequired)}
              label="Timesheet Required"
            />
          </div>
        </div>
      </div>
      {showCreateForm && (
        <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
          <h3 className="text-xl font-semibold text-gray-900 mb-1 mt-2">
            {editingIndex !== null
              ? "Edit Attendance Location"
              : "Create New Attendance Location"}
          </h3>

          <div className="create-form">
            <input
              type="text"
              placeholder="Enter Attendance Location"
              className="input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
            <ButtonWithIcon
              type="button"
              icon="tdesign:send"
              text={editingIndex !== null ? "Update" : "Submit"}
              onClick={handleAddOrUpdate}
              disabled={loading}
              className="submit-button"
            />
          </div>
        </div>
      )}

      <div className="search-and-table">
        <GeneralTable
          className="attendance-location-table"
          columns={columns}
          data={tableData}
          title="Attendance Locations"
        />
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default AttendanceLocation;
